import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DollarSign,
  AlertCircle,
  Clock,
  FileText,
  Plus,
  ArrowRight,
} from 'lucide-react';

interface DashboardSummary {
  totalRevenue: number;
  outstandingAmount: number;
  overdueAmount: number;
  overdueCount: number;
  draftCount: number;
  totalInvoices: number;
  paidCount: number;
  monthlyRevenue: number;
}

interface RecentInvoice {
  id: string;
  invoiceNumber: number;
  accountName: string;
  businessProfileName: string;
  status: string;
  total: number;
  amountPaid: number;
  dueDate: string;
  createdAt: string;
}

const statusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  draft: 'secondary',
  sent: 'info',
  paid: 'success',
  partial: 'warning',
  overdue: 'destructive',
  cancelled: 'secondary',
  void: 'secondary',
};

export function FinanceOverview() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['finance-dashboard-summary'],
    queryFn: async () => {
      const result = await api.getInvoiceSummary();
      return result.data as { summary: DashboardSummary; recentInvoices: RecentInvoice[] };
    },
  });

  const summary = data?.summary;
  const recentInvoices = data?.recentInvoices || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Finance Dashboard</h1>
          <p className="text-muted-foreground">Overview of revenue, invoices, and payments</p>
        </div>
        <Button onClick={() => navigate('/billing/invoices/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Invoice
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(summary?.totalRevenue || 0)}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(summary?.monthlyRevenue || 0)} this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Outstanding</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(summary?.outstandingAmount || 0)}</div>
            <p className="text-xs text-muted-foreground">
              {(summary?.totalInvoices || 0) - (summary?.paidCount || 0)} unpaid invoices
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
            <AlertCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {formatCurrency(summary?.overdueAmount || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {summary?.overdueCount || 0} overdue invoices
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Invoices</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalInvoices || 0}</div>
            <p className="text-xs text-muted-foreground">
              {summary?.draftCount || 0} drafts, {summary?.paidCount || 0} paid
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Invoices</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/billing/invoices')}>
              View All <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentInvoices.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No invoices yet</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => navigate('/billing/invoices/new')}
                >
                  Create your first invoice
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {recentInvoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate(`/billing/invoices/${invoice.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="font-mono text-sm text-muted-foreground">
                        #{invoice.invoiceNumber}
                      </div>
                      <div>
                        <div className="font-medium">{invoice.accountName}</div>
                        <div className="text-xs text-muted-foreground">
                          {invoice.businessProfileName}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="font-medium">{formatCurrency(invoice.total)}</div>
                        <div className="text-xs text-muted-foreground">
                          Due {formatDate(invoice.dueDate)}
                        </div>
                      </div>
                      <Badge variant={statusVariant[invoice.status] || 'secondary'}>
                        {invoice.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate('/billing/invoices/new')}
            >
              <FileText className="h-4 w-4 mr-2" />
              Create Invoice
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate('/billing/business-profiles')}
            >
              <Plus className="h-4 w-4 mr-2" />
              Manage Business Profiles
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate('/billing/applications')}
            >
              <Plus className="h-4 w-4 mr-2" />
              Manage Applications
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate('/billing/payments')}
            >
              <DollarSign className="h-4 w-4 mr-2" />
              View Payments
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
